<template>
  <KeepAlive>
    <div class="overflow-y-auto" ref="applicationsList">
      <!-- Loading state -->
      <div v-if="isPaginationLoading">
        <ul
          :class="[
            'flex flex-col gap-4 w-full mt-4 px-4',
            viewMode === 'grid' ? 'sm:grid sm:grid-cols-2' : ''
          ]"
        >
          <UserApplicationCardSkeleton v-for="n in 6" :key="n" />
        </ul>
      </div>

      <!-- No applications message -->
      <div
        v-else-if="!applicationsStore?.userApplications?.length"
        class="flex flex-col items-center justify-center py-12 px-4 text-center"
      >
        <HeartCrack class="w-12 h-12 text-gray-400 mb-4" />
        <h3 class="text-lg font-medium text-gray-900 mb-1">No applications yet</h3>
        <p class="text-gray-500 max-w-sm">
          Start your journey by applying to open positions <br />
          <a href="/roles" class="text-blue-500 hover:underline">on the job postings page</a>
        </p>
      </div>

      <!-- Applications list -->
      <ul
        v-else
        :class="[
          'flex flex-col gap-4 w-full mt-4 px-4',
          viewMode === 'grid' ? 'sm:grid sm:grid-cols-2' : ''
        ]"
      >
        <UserApplicationCard
          v-for="application in applicationsStore?.userApplications"
          :key="application.id"
          :application="application"
        />
      </ul>

      <!-- Pagination -->
      <PaginationComponent
        v-if="applicationsStore?.userApplications?.length"
        :current-page="currentPage"
        :total-pages="totalPages"
        :use-router="true"
        :additional-query-params="{ ...route.query }"
      />
    </div>
  </KeepAlive>
</template>

<script setup lang="ts">
import { computed, ref, watch, onMounted } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import UserApplicationCard from './UserApplicationCard.vue'
import UserApplicationCardSkeleton from './UserApplicationCardSkeleton.vue'
import { useApplicationsStore } from '@/stores/applications'
import { useViewMode } from '@/composables/useViewMode'
import { HeartCrack } from 'lucide-vue-next'
import PaginationComponent from '@/components/common/PaginationComponent.vue'

const applicationsStore = useApplicationsStore()
const viewMode = useViewMode('applicationsViewMode')
const route = useRoute()
const router = useRouter()

const applicationsList = ref<HTMLUListElement | null>(null)
const currentPage = ref(1)
const isPaginationLoading = ref(false)
const isInitialFetchDone = ref(false)

const totalPages = computed(() => Math.ceil(applicationsStore.userTotalCount / applicationsStore.pageSize))

const scrollToTop = () => {
  applicationsList.value?.scrollTo({
    top: 0,
    behavior: 'smooth'
  })
}

// Add onMounted to explicitly handle direct URL access
onMounted(async () => {
  // When directly accessing a URL with page parameter, make sure we fetch that page
  const pageFromUrl = route.query.page ? parseInt(route.query.page as string) : 1;
  
  // First ensure the correct filter settings are applied from URL
  const currentQuery = route.query;
  
  if (currentQuery.status) {
    applicationsStore.selectedStatus = currentQuery.status as string;
  }
  
  // Update local state
  currentPage.value = pageFromUrl;
  
  // Force-fetch for this direct URL access
  isPaginationLoading.value = true;
  
  try {
    // Clear any existing lastRequestedPage to ensure we fetch fresh data
    applicationsStore.lastRequestedPage = null;
    
    // Force a new fetch with the right page
    await applicationsStore.fetchUserApplications(pageFromUrl, applicationsStore.pageSize);
    
    // Mark as done
    isInitialFetchDone.value = true;
  } finally {
    isPaginationLoading.value = false;
  }
});

// Watch for route changes to handle pagination and application selection
watch(
  () => ({
    page: route.query.page,
    application_id: route.params.application_id,
    status: route.query.status
  }),
  async (newQuery, oldQuery) => {
    // Always get the requested page number from URL
    const requestedPage = newQuery.page ? parseInt(newQuery.page as string) : 1;
    
    // Force direct page access with query changes to always fetch
    const isFirstLoad = !oldQuery;
    const isPageChanged = newQuery.page !== oldQuery?.page;
    const isNavBack = oldQuery?.application_id && !newQuery.application_id;
    const isFilterChanged = 
      newQuery.status !== oldQuery?.status;
    
    // Show loading for all page changes or filter changes
    if (isPageChanged || isFirstLoad || isFilterChanged) {
      isPaginationLoading.value = true;
    }
    
    // Update page state always (even if we decide not to fetch)
    currentPage.value = requestedPage;
    applicationsStore.currentPage = requestedPage;
    
    
    // Force reset of lastRequestedPage to prevent duplicate request suppression
    if (isPageChanged || isFilterChanged) {
      applicationsStore.lastRequestedPage = null;
    }
    
    // Only fetch for certain cases
    if (isFirstLoad || isPageChanged || isNavBack || isFilterChanged) {
      try {
        await applicationsStore.fetchUserApplications(requestedPage, applicationsStore.pageSize);
        
        // Scroll to top when navigating pages (not on first load)
        if ((isPageChanged || isFilterChanged) && !isFirstLoad) {
          scrollToTop();
        }
      } finally {
        isPaginationLoading.value = false;
      }
    } else {
      isPaginationLoading.value = false;
    }
  },
  { immediate: true }
);
</script>
